import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { workQueueRoute } from 'App/common/routeLookup';
import ManualPageTable from 'App/components/ManualPageTable';
import PatientDisplayId from 'App/components/PatientDisplayId/PatientDisplayId';
import ClickLink from 'App/components/styles/ClickLink';
import Table from 'App/components/Table';
import UtcDateCell from 'App/components/Table/Cells/UtcDateCell';
import useCompanySetting from 'App/hooks/useCompanySetting';
import PESService from 'App/services/PESService';
import toast from 'Shared/toast';
import useFetchAssociationQueueAssociations from './hooks/useFetchAssociationQueueAssociations';
import useFetchAssociationQueueEntry from './hooks/useFetchAssociationQueueEntry';

const PatientActionsCell = () => {
    const { associationQueueId } = useParams();
    const navigate = useNavigate();
    const { data: associations = {} } = useFetchAssociationQueueAssociations(associationQueueId);

    return (
        <div>
            <ClickLink
                onClick={async () => {
                    await PESService.enrollAssociationQueueEntry(associationQueueId);
                    toast.success('Patient has been enrolled');
                    navigate(workQueueRoute);
                }}
            >
                Enroll
            </ClickLink>
            {' | '}
            <ClickLink
                onClick={async () => {
                    await PESService.ignoreAssociationQueueEntry(
                        associationQueueId,
                        Object.values(associations).map((a) => ({
                            id: a.id,
                            companyId: a.companyId,
                            divisionId: a.divisionId,
                        }))
                    );
                    toast.success('Patient has been ignored');
                    navigate(workQueueRoute);
                }}
            >
                Ignore
            </ClickLink>
        </div>
    );
};

const patientColumns = [
    {
        Header: 'EHR ID',
        accessor: 'ehrId',
    },
    {
        Header: 'First Name',
        accessor: 'message.FirstName',
    },
    {
        Header: 'Last Name',
        accessor: 'message.LastName',
    },
    {
        Header: 'Sex',
        accessor: 'message.Gender',
    },
    {
        Header: 'Birth Date',
        accessor: 'message.BirthDate',
    },
    {
        Header: 'Address Line 1',
        accessor: 'message.Location.Address1',
    },
    {
        Header: 'City',
        accessor: 'message.Location.City',
    },
    {
        Header: 'State',
        accessor: 'message.Location.State',
    },
    {
        Header: 'Zip',
        accessor: 'message.Location.Zip',
    },
    {
        Header: 'Patient Status',
        accessor: 'message.Status',
    },
    {
        Header: 'Actions',
        Cell: PatientActionsCell,
    },
];

const AssociationActionsCell = ({ row }) => {
    const { associationQueueId } = useParams();
    const navigate = useNavigate();
    return (
        <div>
            <ClickLink
                onClick={async () => {
                    await PESService.associateAssociationQueueEntry(associationQueueId, row.original.id);
                    toast.success('Patient has been associated');
                    navigate(workQueueRoute);
                }}
            >
                Associate
            </ClickLink>
        </div>
    );
};
AssociationActionsCell.propTypes = {
    row: PropTypes.shape({
        original: PropTypes.shape({
            id: PropTypes.string,
        }),
    }).isRequired,
};

const associationsColumns = [
    {
        Header: 'Patient ID',
        disableFilters: true,
        accessor: (row) => <PatientDisplayId patientId={row.id} fallback="--" />,
    },
    {
        Header: 'First Name',
        accessor: 'firstName',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'Last Name',
        accessor: 'lastName',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'Sex',
        accessor: 'gender',
        disableFilters: true,
        fallbackToDashDash: true,
        Cell: ({ value }) => capitalize(value),
    },
    {
        Header: 'Birth Date',
        accessor: 'birthDate',
        disableFilters: true,
        fallbackToDashDash: true,
        Cell: UtcDateCell,
    },
    {
        Header: 'Address Line 1',
        accessor: 'addressLine1',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'City',
        accessor: 'city',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'State',
        accessor: 'state',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'Zip',
        accessor: 'zip',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'Patient Status',
        accessor: 'status',
        disableFilters: true,
        fallbackToDashDash: true,
    },
    {
        Header: 'Actions',
        disableFilters: true,
        Cell: AssociationActionsCell,
    },
];

const UnassignedPatient = () => {
    const { associationQueueId } = useParams();
    const { data: ehrPrimaryIdType } = useCompanySetting('ehrPrimaryIdType', { defaultValue: '' });

    const { data: patient = {}, isLoading: isPatientLoading } = useFetchAssociationQueueEntry(associationQueueId);
    const ehrId = patient.message?.Identifiers?.find((i) => i.IDType === ehrPrimaryIdType)?.ID;

    const { data: associations = {}, isLoading: isAssociationsLoading } =
        useFetchAssociationQueueAssociations(associationQueueId);

    return (
        <div className="container">
            <h2>Unassigned Patient</h2>
            <ManualPageTable
                columns={patientColumns}
                data={[{ ...patient, ehrId }]}
                isLoading={isPatientLoading}
                pageSizeOptions={[]}
            />

            <h2>Associated Patients</h2>
            <Table
                columns={associationsColumns}
                data={Object.values(associations)}
                isLoading={isAssociationsLoading}
                placeholderText="No Associated Patients"
            />
        </div>
    );
};

export default UnassignedPatient;
